import { RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import { ContextualMenu } from '../../../../../components/menus/ContextualMenu';
import ConfirmDialog from '../../../../../dialogs/ConfirmDialog';
import { Project } from '../../../../../domain/backoffice/project/Project';
import { useUpdateProjectTraffic } from '../../../../../hooks/backoffice/projects/useUpdateProjectTraffic';
import Text from '../../../../../react-components/Text';
import { BackofficeClientsProjectsManageVehiclesDialog } from './BackofficeClientsProjectsManageVehiclesDialog';
import { BackofficeProjectsManageWhatsAppTemplateDialog } from './BackofficeProjectsManageWhatsAppTemplateDialog';

type BackofficeClientsProjectActionsMenuContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type BackofficeClientsProjectActionsMenuType = `contextual` | `popover`;

type BackofficeClientsProjectActionsMenuProps = {
  project: Project;
  type?: BackofficeClientsProjectActionsMenuType;
  contextAnchor?: BackofficeClientsProjectActionsMenuContextAnchor;
  showManageVehicles?: boolean;
  showEnableTraffic?: boolean;
  showDisableTraffic?: boolean;
  showManageWhatsApp?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  refreshAfterUpdate?: () => void;
  setContextAnchor?: (context?: BackofficeClientsProjectActionsMenuContextAnchor) => void;
};

export const BackofficeClientsProjectActionsMenu = ({
  project,
  type = `contextual` as BackofficeClientsProjectActionsMenuType,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  showManageVehicles = true,
  showEnableTraffic = true,
  showDisableTraffic = true,
  showManageWhatsApp = true,
  popoverProps,
  refreshAfterUpdate,
  setContextAnchor = () => {},
}: BackofficeClientsProjectActionsMenuProps) => {
  const { success, run: updateProjectTraffic } = useUpdateProjectTraffic();
  const [dialog, setDialog] = useState<
    `manage_vehicles` | `enable_traffic` | `disable_traffic` | `whatsapp_template` | undefined
  >(undefined);

  useEffect(() => {
    if (success) {
      if (setContextAnchor) {
        setContextAnchor();
      }

      if (refreshAfterUpdate) {
        refreshAfterUpdate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  if (!project) return null;

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="routes-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showManageVehicles
                ? []
                : [
                    {
                      key: `manage_vehicles`,
                      width: 240,
                      onClick: () => {
                        setDialog(`manage_vehicles`);
                      },
                      content: (
                        <Text intlId="backoffice.clients.projects.manage_vehicles" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showDisableTraffic || !project.resources.traffic
                ? []
                : [
                    {
                      key: `disable_traffic`,
                      width: 240,
                      onClick: () => {
                        setDialog(`disable_traffic`);
                      },
                      content: (
                        <Text intlId="backoffice.clients.projects.disable_traffic" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showEnableTraffic || project.resources.traffic
                ? []
                : [
                    {
                      key: `enable_traffic`,
                      width: 240,
                      onClick: () => {
                        setDialog(`enable_traffic`);
                      },
                      content: (
                        <Text intlId="backoffice.clients.projects.enable_traffic" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showEnableTraffic
                ? []
                : [
                    {
                      key: `whatsapp_template`,
                      width: 240,
                      onClick: () => {
                        setDialog(`whatsapp_template`);
                      },
                      content: <Text intlId="backoffice.projects.whatsapp_template" style={{ cursor: `pointer` }} />,
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}
      {type === `contextual` && (
        <>
          {contextAnchor ? (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor();
                }
              }}
              options={[
                ...(!showManageVehicles
                  ? []
                  : [
                      {
                        key: `manage_vehicles`,
                        text: (
                          <Text intlId="backoffice.clients.projects.manage_vehicles" style={{ cursor: `pointer` }} />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`manage_vehicles`);
                        },
                      },
                    ]),
                ...(!showDisableTraffic || project.resources.traffic
                  ? []
                  : [
                      {
                        key: `disable_traffic`,
                        text: (
                          <Text intlId="backoffice.clients.projects.disable_traffic" style={{ cursor: `pointer` }} />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`disable_traffic`);
                        },
                      },
                    ]),
                ...(!showEnableTraffic || !project.resources.traffic
                  ? []
                  : [
                      {
                        key: `enable_traffic`,
                        text: (
                          <Text intlId="backoffice.clients.projects.enable_traffic" style={{ cursor: `pointer` }} />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`enable_traffic`);
                        },
                      },
                    ]),
                ...(!showEnableTraffic
                  ? []
                  : [
                      {
                        key: `whatsapp_template`,
                        text: <Text intlId="backoffice.projects.whatsapp_template" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`whatsapp_template`);
                        },
                      },
                    ]),
              ]}
            />
          ) : null}
        </>
      )}

      {dialog === `manage_vehicles` ? (
        <BackofficeClientsProjectsManageVehiclesDialog
          openDialog={dialog === `manage_vehicles`}
          project={project}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}

      {dialog === `enable_traffic` ? (
        <ConfirmDialog
          title={<Text variant="h5" intlId="backoffice.clients.projects.enable_traffic.warning_dialog.title" />}
          description={
            <Text variant="body" intlId="backoffice.clients.projects.enable_traffic.warning_dialog.content" />
          }
          open={Boolean(dialog === `enable_traffic`)}
          onCloseDialog={() => setDialog(undefined)}
          onConfirm={() => {
            updateProjectTraffic({
              projectId: project.id,
              traffic: true,
            });

            setDialog(undefined);
          }}
        />
      ) : null}

      {dialog === `disable_traffic` ? (
        <ConfirmDialog
          title={<Text variant="h5" intlId="backoffice.clients.projects.disable_traffic.warning_dialog.title" />}
          description={
            <Text variant="body" intlId="backoffice.clients.projects.disable_traffic.warning_dialog.content" />
          }
          open={Boolean(dialog === `disable_traffic`)}
          onCloseDialog={() => setDialog(undefined)}
          onConfirm={() => {
            updateProjectTraffic({
              projectId: project.id,
              traffic: false,
            });

            setDialog(undefined);
          }}
        />
      ) : null}

      {dialog === `whatsapp_template` ? (
        <BackofficeProjectsManageWhatsAppTemplateDialog
          openDialog={dialog === `whatsapp_template`}
          project={project}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}
    </>
  );
};
